import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Edit3,
  X,
  Save,
  Search,
  RefreshCcw,
  Info,
  Hash,
  Layers,
  ChevronRight,
  Scale,
  List,
  LayoutGrid,
  Trash2,
  Eye,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Unidades de Medida (units_of_measure)
 * Estandarizado al layout Productos / Gastos:
 * Header + CTA / KPIs / Panel Carga (Izq) / Listado (Der)
 */
const UnidadesMedida = () => {
  const { profile } = useAuth();

  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI layout (modelo Productos)
  const [layout, setLayout] = useState('lista'); // lista | grilla
  const [searchTerm, setSearchTerm] = useState('');

  // Formulario Inline (Sin modales)
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', abbreviation: '' });

  useEffect(() => {
    if (profile?.organization_id) fetchUnidades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const fetchUnidades = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('units_of_measure')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (error) throw error;
      setUnidades(data || []);
    } catch (error) {
      toast.error('Error al cargar unidades');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (u) => {
    setEditingId(u.id);
    setFormData({ name: u.name, abbreviation: u.abbreviation });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', abbreviation: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.abbreviation) {
      return toast.error('Complete nombre y abreviatura');
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.toUpperCase(),
        abbreviation: formData.abbreviation, // Case sensitive usually preferred for units (e.g., 'mm', 'Kg') but forcing logic if needed
        organization_id: profile.organization_id
      };

      if (editingId) {
        const { error } = await supabase
          .from('units_of_measure')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Unidad actualizada');
      } else {
        const { error } = await supabase
          .from('units_of_measure')
          .insert([payload]);
        if (error) throw error;
        toast.success('Unidad creada');
      }
      handleCancelEdit();
      fetchUnidades();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar unidad? Esto puede afectar productos asociados.")) return;
    try {
      const { error } = await supabase.from('units_of_measure').delete().eq('id', id);
      if (error) throw error;
      toast.success('Unidad eliminada');
      fetchUnidades();
    } catch (error) {
      toast.error('No se puede eliminar (en uso)');
    }
  };

  // --- FILTROS ---
  const filteredUnidades = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return unidades.filter(u => 
      u.name.toLowerCase().includes(q) || 
      u.abbreviation.toLowerCase().includes(q)
    );
  }, [unidades, searchTerm]);

  // --- KPIS ---
  const kpis = useMemo(() => {
    const total = unidades.length;
    // Ejemplo de KPI simple: cantidad de unidades "base" vs compuestas (si hubiera lógica)
    // Por ahora conteo simple
    return { total };
  }, [unidades]);

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Scale size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Unidades de Medida</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Magnitudes físicas para inventario.
            </p>
          </div>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Unidades</p>
            <Layers size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Definidas</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sistema</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">Activo</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Configuración OK</p>
        </div>

        <div className="imp-kpi bg-[#51A7FD]">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-white/80">Información</p>
                <Info size={16} className="text-white" />
            </div>
            <p className="mt-1.5 text-[10px] font-bold text-white leading-relaxed">
                Las unidades se utilizan para definir precios y stock. No elimine unidades en uso.
            </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* COLUMNA IZQUIERDA: FORMULARIO (Tarjeta estilo Gastos) */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  {editingId ? 'Editar Unidad' : 'Nueva Unidad'}
                </span>
              </div>
              {editingId && (
                <button onClick={handleCancelEdit} className="text-[9px] font-bold uppercase text-white/60 hover:text-white flex items-center gap-1">
                    <X size={12}/> Cancelar
                </button>
              )}
            </div>

            <div className="p-4">
                <form onSubmit={handleSave} className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre Descriptivo</label>
                        <input 
                            type="text"
                            placeholder="EJ: KILOGRAMO, METRO..."
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                            autoFocus
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Abreviatura (Corta)</label>
                        <input 
                            type="text"
                            placeholder="EJ: KG, M, UN..."
                            value={formData.abbreviation}
                            onChange={(e) => setFormData({...formData, abbreviation: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                        />
                    </div>

                    <div className="pt-2">
                        <button 
                            type="submit" 
                            disabled={saving}
                            className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg
                                ${editingId 
                                    ? 'bg-[#51A7FD] text-white hover:bg-blue-600' 
                                    : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-500/20'}`}
                        >
                            {saving ? <RefreshCcw className="animate-spin" size={14} /> : <Save size={16} />}
                            {editingId ? 'Actualizar' : 'Guardar Unidad'}
                        </button>
                    </div>
                </form>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: LISTADO */}
        <div className="lg:col-span-2 space-y-4">
            
            {/* BARRA DE FILTROS */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center justify-between gap-3">
                <div className="relative flex-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                        <Search size={10} /> Buscar
                    </label>
                    <div className="relative">
                        <input 
                            type="text"
                            placeholder="NOMBRE O ABREVIATURA..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] uppercase text-slate-700"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                    </div>
                </div>

                <div className="flex items-end h-[50px]">
                    <div className="flex bg-slate-50 rounded-xl p-1 border border-slate-100">
                        <button 
                            onClick={() => setLayout('lista')}
                            className={`p-2 rounded-lg transition-all ${layout === 'lista' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <List size={16} />
                        </button>
                        <button 
                            onClick={() => setLayout('grilla')}
                            className={`p-2 rounded-lg transition-all ${layout === 'grilla' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <LayoutGrid size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* CONTENIDO LISTA/GRILLA */}
            <div className="w-full">
                {loading ? (
                    <div className="h-40 flex flex-col items-center justify-center text-slate-400 gap-2">
                        <RefreshCcw className="animate-spin" size={20} />
                        <p className="text-[10px] font-black uppercase tracking-widest">Cargando unidades...</p>
                    </div>
                ) : filteredUnidades.length === 0 ? (
                    <div className="h-40 flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-100 rounded-2xl">
                        <Scale size={32} strokeWidth={1} />
                        <p className="text-[10px] font-black uppercase tracking-widest">Sin resultados</p>
                    </div>
                ) : layout === 'lista' ? (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto imp-table-wrap">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        <th className="px-5 py-3 pl-5">Nombre</th>
                                        <th className="px-4 py-3 text-center">Abrev.</th>
                                        <th className="px-4 py-3 text-right pr-5">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredUnidades.map(u => (
                                        <tr key={u.id} className="hover:bg-blue-50/20 transition-all group">
                                            <td className="px-5 py-3 pl-5">
                                                <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">{u.name}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="inline-block bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200 text-[10px] font-bold">
                                                    {u.abbreviation}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 pr-5 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => handleEdit(u)} className="p-1.5 text-slate-400 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all">
                                                        <Edit3 size={14} />
                                                    </button>
                                                    <button onClick={() => handleDelete(u.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {filteredUnidades.map(u => (
                            <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all group relative">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-tight mb-1">{u.name}</h3>
                                        <span className="text-[10px] font-bold text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">{u.abbreviation}</span>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEdit(u)} className="p-1.5 bg-slate-50 hover:bg-white border hover:border-blue-200 rounded-lg text-slate-400 hover:text-[#51A7FD]">
                                            <Edit3 size={12} />
                                        </button>
                                        <button onClick={() => handleDelete(u.id)} className="p-1.5 bg-slate-50 hover:bg-white border hover:border-rose-200 rounded-lg text-slate-400 hover:text-rose-500">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
      </div>

      {/* --- MODAL ORIGINAL (DEPRECADO, NO BORRADO) --- */}
      {/* Se mantiene el código comentado o en bloque condicional falso por instrucción estricta de no borrar */}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
};

export default UnidadesMedida;